import { useMutation, useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import Drawer from "@mui/material/Drawer";
import {
  Button,
  LoadingSpinner,
  Notification,
  ToastContainer,
  Toggle,
} from "pepsico-ds";
import { useCallback, useEffect, useState } from "react";
import {
  CreateCodeGroupMutation,
  UpdateCodeGroupMutation,
} from "../../graphql/mutations/codeGroupMutations";
import { getCampaignsTaggedToCodeGroup } from "../../graphql/queries/getCampaignListing";
import { updateUiSchema } from "../../utils/commons";
import {
  CODE_GROUP_ADMINISTRATION_STATUS,
  CODE_GROUP_CREATION_TYPE,
  CODE_GROUP_LIFE_CYCLE_STATUS,
} from "../../utils/constants";
import JsonFormsWrapper from "../jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { usePartnerCodeGroupForm } from "../partners/Modals/PartnerCodeGroupForm/usePartnerCodeGroupForm";
import { jsonSchemaForPtr, uiSchemaForPtr } from "./codeGroupUtils";
import "./createCodeGroupModal.scss";

/**
 * CodeGroupCommonSetupModal component.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.showModal - Flag indicating whether the modal should be shown.
 * @param {function} props.setShowModal - Function to set the showModal state.
 * @params {function} props.refetchListing - Function to refetch the listing.
 * @params {boolean} props.loadCodeGroupLoader - Flag indicating whether the code group data is loading.
 * @params {Object} props.codeGroupData - The code group data.
 * @params {function} props.setCodeGroupData - Function to set the code group data
 * @returns {JSX.Element} The CodeGroupCommonSetupModal component.
 */
export default function CodeGroupCommonSetupModal({
  showModal,
  setShowModal,
  setToast,
  refetchListing,
  loadCodeGroupLoader,
  codeGroupData,
  setCodeGroupData,
}) {
  const [alreadyCreatedData, setAlreadyCreatedData] = useState({});
  const [modalToast, setModalToast] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [executeUpdateCodeGroup] = useMutation(UpdateCodeGroupMutation);
  const [executeCreateCodeGroup] = useMutation(CreateCodeGroupMutation);
  const { handleUploadFileCall, handleDownloadFile, validateUploadFile } =
    usePartnerCodeGroupForm();

  const { data: attachedCampaigns } = useQuery(getCampaignsTaggedToCodeGroup, {
    variables: {
      filter: { codeGroups: [codeGroupData?.codeGroupId] },
      skip: !codeGroupData?.codeGroupId,
    },
  });

  useEffect(() => {
    if (!alreadyCreatedData.codeGroupId && codeGroupData?.codeGroupId) {
      setAlreadyCreatedData(codeGroupData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeGroupData]);

  const onCloseModal = () => {
    setShowModal((prev) => ({ ...prev, setupModal: false }));
    setCodeGroupData({});
  };

  // TODO: Uncomment this function when needed
  // const resetForm = useCallback(() => {
  //   setCodeGroupData({});
  // }, [setCodeGroupData]);

  const setModalToastFn = useCallback(
    (text, type, id) => {
      setToast([
        {
          text: text,
          type: type,
          id: id,
        },
      ]);
    },
    [setToast]
  );
  const createCodeGroupPayload = useCallback(
    (lifecycleStatus = "QUEUED") => {
      return {
        administrationStatus: codeGroupData?.administrationStatus
          ? codeGroupData?.administrationStatus
          : CODE_GROUP_ADMINISTRATION_STATUS.ENABLED,
        lifecycleStatus: lifecycleStatus,
        creationType: CODE_GROUP_CREATION_TYPE.PRE_GENERATED,
        basicSetup: {
          codeGroupName: codeGroupData?.codeGroupName,
          codeActiveStartDate: codeGroupData?.codeActiveStartDate,
          codeActiveEndDate: codeGroupData?.codeActiveEndDate,
        },
        producerPackaging: {
          producerCompany: codeGroupData.codeGroupName,
          codesPrintedCount: 0,
        },
        dummyCodes: {
          allowDummyCodes: codeGroupData.allowDummyCodes,
          dummyCodeMessage: codeGroupData.dummyCodeMessage || "",
          dummyCodePercent: codeGroupData.dummyCodePercent || 0,
        },
        codeSpecifications: {
          codeLength: 10,
          usePrefix: codeGroupData.usePrefix,
          prefixVal: codeGroupData.prefixVal || null,
          useSuffix: codeGroupData.useSuffix,
          suffixVal: codeGroupData.suffixVal || null,
          caseSensitive: codeGroupData.caseSensitive,
          excludeCharacters: codeGroupData.excludeCharacters,
          excludeCharacterValues: codeGroupData.excludeCharacterValues || "",
        },
      };
    },
    [codeGroupData]
  );
  const fileUpload = useCallback(
    async (lifecycleStatus, codeGroupId) => {
      try {
        // Extract valid files from rewardItemFileData
        const files = codeGroupData?.rewardItemFileData || [];

        // Return early if no files to upload
        if (files.length === 0) {
          return { status: true, message: "No files to upload" };
        }

        // Upload files
        const uploadResponse = await handleUploadFileCall(files, codeGroupId);

        // Handle upload failure
        if (!uploadResponse?.status) {
          return {
            status: false,
            message: uploadResponse?.message || "File upload failed",
          };
        }

        // Update file data in state on success
        if (uploadResponse.data && Array.isArray(uploadResponse.data)) {
          // Create a copy to avoid direct state mutation
          const updatedFileData = [...(codeGroupData.rewardItemFileData || [])];

          uploadResponse.data.forEach((uploadedFile) => {
            const fileIndex = updatedFileData.findIndex(
              (f) => f.name === uploadedFile.name
            );

            if (fileIndex >= 0) {
              // Replace existing file
              updatedFileData[fileIndex] = uploadedFile;
            } else {
              // Add new file
              updatedFileData.push(uploadedFile);
            }
          });

          // Update state with new files
          setCodeGroupData({
            ...codeGroupData,
            rewardItemFileData: updatedFileData,
          });
        }

        return { status: true, message: "Files uploaded successfully" };
      } catch (error) {
        return {
          status: false,
          message:
            error?.message || "An unexpected error occurred during file upload",
        };
      }
    },
    [codeGroupData, handleUploadFileCall, setCodeGroupData]
  );

  const performPublishCodeGroup = useCallback(
    async (lifecycleStatus = "QUEUED") => {
      try {
        let codeGroupId = codeGroupData.codeGroupId;
        let responseData;
        let successMessage;

        // Handle edit vs create code group scenarios
        if (codeGroupId) {
          // Update existing code group
          const response = await executeUpdateCodeGroup({
            variables: {
              codeGroup: createCodeGroupPayload(lifecycleStatus),
              id: codeGroupId,
            },
          });

          responseData = response?.data;
          successMessage = "Successfully updated code group.";
        } else {
          // Create new code group
          const response = await executeCreateCodeGroup({
            variables: {
              codeGroup: createCodeGroupPayload(lifecycleStatus),
            },
          });

          codeGroupId = response?.data?.createCodeGroup?.codeGroupId;
          responseData = response?.data;
          successMessage = "Successfully created code group.";
        }

        // Handle file upload if files exist
        let fileUploadStatus = { status: true, message: "" };
        if (codeGroupData?.rewardItemFileData?.length > 0 && codeGroupId) {
          const uploadResult = await fileUpload(lifecycleStatus, codeGroupId);
          // Ensure uploadResult is not undefined before accessing its properties
          fileUploadStatus = uploadResult || {
            status: false,
            message: "File upload failed",
          };
        }

        // Return result with appropriate message
        return {
          isSuccess: Boolean(responseData) && Boolean(fileUploadStatus?.status),
          id: codeGroupId,
          message:
            responseData && fileUploadStatus?.status
              ? successMessage
              : fileUploadStatus?.message || "Operation failed",
        };
      } catch (error) {
        return {
          isSuccess: false,
          message: error?.message || "An unexpected error occurred",
        };
      }
    },
    [
      codeGroupData,
      fileUpload,
      createCodeGroupPayload,
      executeCreateCodeGroup,
      executeUpdateCodeGroup,
    ]
  );
  const callPerformPublishCodeGroup = useCallback(
    async (lifecycleStatus) => {
      const response = await performPublishCodeGroup(lifecycleStatus);
      if (response.isSuccess) {
        setShowModal({ showModal, setupModal: false });
        setModalToastFn(response.message, "success", 4);
        setCodeGroupData({});
        refetchListing();
      } else {
        setShowModal({ showModal, setupModal: true });
        setModalToastFn(response.message, "error", 5);
      }
      setIsSubmitting(false);
    },
    [
      setShowModal,
      refetchListing,
      performPublishCodeGroup,
      setModalToastFn,
      showModal,
      setCodeGroupData,
    ]
  );

  const validateCodeGroupData = (codeGroupData) => {
    let missingFields = [];

    if (!codeGroupData.codeGroupName?.trim()) {
      missingFields.push("Code Group Name");
    }
    if (!codeGroupData.codeActiveStartDate) {
      missingFields.push("Start Date");
    }
    if (!codeGroupData.codeActiveEndDate) {
      missingFields.push("End Date");
    }
    if (
      !codeGroupData?.codeGroupId &&
      !(codeGroupData?.rewardItemFileData?.length > 0)
    ) {
      missingFields.push("File");
    }
    return missingFields.length > 0
      ? [
          `${missingFields.join(", ")} ${missingFields.length > 1 ? "are" : "is"} required`,
        ]
      : [];
  };

  const getCodeGroupUISchema = useCallback(() => {
    if (codeGroupData?.codeGroupId && codeGroupData.compositeStatus === "ACTIVE") {
      let targetScopesObject = {
        "#/properties/codeActiveStartDate": {
          disabled: true,
        },
        "#/properties/codeActiveEndDate": {
          minimum: codeGroupData.codeActiveEndDate,
        },
      };
      return updateUiSchema(
        JSON.parse(JSON.stringify(uiSchemaForPtr)),
        targetScopesObject
      );
    }

    if (codeGroupData?.codeGroupId && codeGroupData.compositeStatus === "INACTIVE") {
      let targetScopesObject = {
        "#/properties/codeActiveStartDate": {
          disabled: true,
        },
        "#/properties/codeActiveEndDate": {
          minimum: codeGroupData.codeActiveEndDate,
        },
      };
      return updateUiSchema(
        JSON.parse(JSON.stringify(uiSchemaForPtr)),
        targetScopesObject
      );
    }

    return uiSchemaForPtr;
  }, [
    codeGroupData?.codeGroupId,
    codeGroupData.codeActiveEndDate,
    codeGroupData.compositeStatus,
  ]);

  const checkToggleEnableDisable = useCallback(() => {
    if (
      attachedCampaigns?.listCampaigns?.length > 0 &&
      codeGroupData?.codeGroupId &&
      codeGroupData.compositeStatus === "ACTIVE"
    ) {
      return true;
    }
  }, [attachedCampaigns, codeGroupData?.codeGroupId, codeGroupData.compositeStatus]);

  const handleSubmitButton = useCallback(
    async (enableDraftFlow, lifecycleStatus) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      const errors = validateCodeGroupData(codeGroupData);
      // Display errors if any
      if (errors.length > 0) {
        setIsSubmitting(false);
        setModalToast(
          errors.map((error, index) => ({
            text: error,
            type: "error",
            id: `validation-error-${index}`,
          }))
        );
        return;
      }
      const isComingFromDraft =
        lifecycleStatus === CODE_GROUP_LIFE_CYCLE_STATUS.DRAFT;
      const updatedLifeCycleStatus = isComingFromDraft
        ? CODE_GROUP_LIFE_CYCLE_STATUS.QUEUED
        : lifecycleStatus;

      const files = codeGroupData?.rewardItemFileData || [];

      // Only validate files if rewardItemFileData has entries
      if (files?.length > 0) {
        const validateFileUpload = await validateUploadFile(files);
        if (validateFileUpload?.data?.is_valid) {
          callPerformPublishCodeGroup(updatedLifeCycleStatus);
        } else {
          if (
            validateFileUpload?.issues &&
            Array.isArray(validateFileUpload?.issues) &&
            validateFileUpload?.issues.length > 0
          ) {
            // Process each error and display toast notifications
            let errorMessage = validateFileUpload?.issues
              .map((error) => error.detail)
              .join(". ");
            // Add a period at the end if needed
            if (!errorMessage.endsWith(".")) {
              errorMessage += ".";
            }
            // Display the concatenated error message
            setModalToastFn(errorMessage, "error", 6);
          } else {
            // Fallback error message if no detailed errors are available
            setModalToastFn(
              "Error while uploading file. Please check file format and try again.",
              "error",
              6
            );
          }
        }
      } else {
        // If no files to validate, proceed with code group creation/update
        callPerformPublishCodeGroup(updatedLifeCycleStatus);
      }
    },
    [
      isSubmitting,
      codeGroupData,
      setModalToastFn,
      validateUploadFile,
      callPerformPublishCodeGroup,
      // validateIsAttachedCampaigns,
    ]
  );

  return (
    <Drawer
      open={showModal.setupModal}
      onClose={onCloseModal}
      anchor="right"
      classes={{ paper: "codegroupsetup-drawer" }}
    >
      <div className="codegroupsetup-drawer-header">
        <h2
          className="codegroupsetup-drawer-title"
          data-testid="create-code-group-modal"
        >
          {codeGroupData?.codeGroupId ? "Update" : "Create"} Code Group
        </h2>
        <button
          type="button"
          aria-label="Close"
          className="codegroupsetup-drawer-close"
          onClick={onCloseModal}
        >
          <CloseIcon data-testid="closeIcon" />
        </button>
      </div>
      {modalToast.length > 0 && (
        <div className="codegroupsetup-drawer-toast">
          <ToastContainer
            data={modalToast}
            removeToast={() => setModalToast([])}
            showActionIcon
          />
        </div>
      )}
      {!loadCodeGroupLoader ? (
        <div className="codegroupsetup-drawer-content">
          <div className="my-5 create-modal-wrapper">
            <JsonFormsWrapper
              jsonschema={jsonSchemaForPtr}
              uischema={getCodeGroupUISchema()}
              data={codeGroupData}
              onChange={(val) => {
                setIsSubmitting(false);
                setCodeGroupData(val);
              }}
            />
          </div>
          <Button
            size="small"
            variant="secondary"
            onClick={() => handleDownloadFile("text/csv", "sampleFile.csv")}
            className="download-sample-file"
          >
            Download sample file <DownloadIcon fontSize="small" />
          </Button>
          <Notification
            className="code-limit-info-note"
            type="info"
            title={
              <div className="code-limit-info-content">
                <p className="code-limit-info-title">Code Usage Format Guide</p>
                <p>
                  Upload codes using the format:&nbsp;
                  <code>&lt;Code&gt;,&lt;Usage Limit&gt;</code>
                </p>
                <p>For Usage Limit:</p>
                <ul>
                  <li>
                    <strong>0</strong> = Unlimited Usage
                  </li>
                  <li>
                    <strong>1 to N (e.g., 5, 100)</strong> = Limited Code Usage
                  </li>
                  <li>
                    <strong>No usage limit specified</strong> = Single-use code
                    (default)
                  </li>
                </ul>
                <p className="code-limit-info-title">Examples</p>
                <ul>
                  <li>
                    <code>CODE12345,0</code> &rarr; Unlimited usage
                  </li>
                  <li>
                    <code>CODE12221,5</code> &rarr; Can be used 5 times
                  </li>
                  <li>
                    <code>CODE12666,</code> &rarr; Single-use code
                  </li>
                </ul>
              </div>
            }
            text=""
          />
        </div>
      ) : (
        <div className="codegroupsetup-drawer-content display-flex align-items-center justify-content-center">
          <LoadingSpinner
            data-testid="loading-spinner"
            size={{ height: "50px", width: "50px" }}
          />
        </div>
      )}
      <div className="codegroupsetup-drawer-footer">
        {[
          CODE_GROUP_LIFE_CYCLE_STATUS.ACTIVE,
          CODE_GROUP_LIFE_CYCLE_STATUS.INACTIVE,
        ].includes(codeGroupData?.compositeStatus) && (
          <Toggle
            className="toggleStyles mr-3"
            label={
              codeGroupData?.administrationStatus ===
              CODE_GROUP_ADMINISTRATION_STATUS.ENABLED
                ? "Active"
                : "In Active"
            }
            disabled={checkToggleEnableDisable()}
            onUpdate={(val) => {
              setCodeGroupData({
                ...codeGroupData,
                administrationStatus: val
                  ? CODE_GROUP_ADMINISTRATION_STATUS.ENABLED
                  : CODE_GROUP_ADMINISTRATION_STATUS.DISABLED,
              });
            }}
            size="medium"
            checked={
              codeGroupData?.administrationStatus ===
              CODE_GROUP_ADMINISTRATION_STATUS.ENABLED
            }
          />
        )}
        <Button
          variant="primary"
          className="jufstify-content-space-around"
          disabled={isSubmitting || loadCodeGroupLoader}
          onClick={() => handleSubmitButton(false, codeGroupData.lifecycleStatus)}
        >
          {codeGroupData?.codeGroupId ? "Update" : "Create"}
        </Button>
      </div>
    </Drawer>
  );
}
